/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadFinalizedBagsPendingOutscanReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import constants from "../../../../constants/constants";

const { ROLES } = constants;
const { HUB_ADMIN, HO_ADMIN } = ROLES;

const FinalizedBagsPendingOutscanReport = () => {
    // Global request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(2, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedPremiseHubs, setSelectedPremiseHubs] = useState([]);
    const [selectedDestinationHubs, setSelectedDestinationHubs] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["50", "100", "200", "500"],
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [headerHubList, setHeaderHubList] = useState([]);
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Finalized Bags Pending Outscan Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);

    // Table columns definition
    const columns = [
        {
            title: "Bag Number",
            dataIndex: "bagNumber",
            key: "bagNumber",
            width: 180,
            fixed: "left",
            render: (text, record) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        {
            title: "Source Hub Name",
            dataIndex: "premiseName",
            key: "premiseName",
            width: 200
        },
        {
            title: "Destination Hub Name",
            dataIndex: "destinationHubName",
            key: "destinationHubName",
            width: 200
        },
        {
            title: "Finalized User Name",
            dataIndex: "finalizedUserName",
            key: "finalizedUserName",
            width: 180
        },
        {
            title: "Bag Finalized Time",
            dataIndex: "bagFinalizedTime",
            key: "bagFinalizedTime",
            width: 180,
            render: (text) => text ? moment(text, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD HH:mm:ss") : "-"
        },
    ];

    // Combined initial setup useEffect
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
            } catch (error) {
                console.error("Error loading hub list:", error);
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
            const role = user?.roles?.[0];
            const isSpecialRole = role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR";

            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedPremiseHubs([newActiveHubId]);
            } else if (isSpecialRole) {
                setSelectedPremiseHubs([]);
            } else {
                setSelectedPremiseHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const role = user?.roles?.[0];
        setHubUser(role);

        const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
        const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

        if (!hubInitializedRef.current) {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            
            if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                setSelectedPremiseHubs([currentActiveHubId]);
            } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                setSelectedPremiseHubs([]);
            } else {
                setSelectedPremiseHubs(["ALL"]);
            }
            hubInitializedRef.current = true;
        }
    }, [headerHubList]); // Depend on headerHubList to ensure we have options available

    // Debounced effect for report fetching - prevents rapid API calls during filter changes
    useEffect(() => {
        // Wait for hubUser to be initialized
        if (!hubUser) {
            return;
        }

        // Debounce the API call to prevent multiple rapid calls when resetting filters
        const timer = setTimeout(() => {
            if (dateRange && dateRange[0] && dateRange[1]) {
                fetchReport();
            } else {
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [hubUser, selectedPremiseHubs, selectedDestinationHubs, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
        const { signal, requestId } = createNewRequest("finalized-bags-pending-outscan-report");

        try {
            setLoading(true);
            setReportData([]); // Clear previous data

            // Validate dates
            if (!dateRange || !Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
                setLoading(false);
                message.warning("Please select a valid date range");
                return;
            }

            // Ensure we have moment objects
            const startDate = moment(dateRange[0]);
            const endDate = moment(dateRange[1]);

            if (!startDate.isValid() || !endDate.isValid()) {
                setLoading(false);
                message.warning("Invalid date range selected");
                return;
            }

            // Resolve Source Hubs (selectedPremiseHubs) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedPremiseIds = selectedPremiseHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedPremiseHubs;

            const params = {
                fromDate: startDate.format("YYYY-MM-DD"),
                toDate: endDate.format("YYYY-MM-DD"),
                ...(resolvedPremiseIds.length > 0 && { premiseIds: resolvedPremiseIds.join(",") }),
                ...(selectedDestinationHubs.length > 0 && { destinationHubIds: selectedDestinationHubs.join(",") }),
                page: (pagination.current - 1), // Convert to 0-based
                size: pagination.pageSize
            };

            console.log("Finalized Bags Pending Outscan Report params:", params);

            const response = await ReportsService.getFinalizedBagsPendingOutscanReport(params, { signal });

            // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring response");
                return;
            }

            setLastsynced(response.lastRefreshedAt);
            setNextScheduledRefresh(response.nextScheduledRefresh);
            setReportData(response.data || []);
            setPagination(prev => ({
                ...prev,
                total: response.total || 0,
            }));

        } catch (error) {
            // 🔥 DON"T UPDATE STATE IF REQUEST WAS CANCELLED
            if (error.name === "AbortError") {
                console.log("🔥 Request was cancelled at HTTP level - no state update");
                return;
            }

            // Only update if this is still the latest request
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring error");
                return;
            }

            console.error("Failed to fetch Finalized Bags Pending Outscan report:", error);
            message.error("Failed to fetch report data");
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };


    const handleDownload = async () => {
        if (!dateRange || !Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
            message.warning("Please select a valid date range");
            return;
        }

        try {
            setDownloadLoading(true);

            // Ensure we have moment objects
            const startDate = moment(dateRange[0]);
            const endDate = moment(dateRange[1]);

            if (!startDate.isValid() || !endDate.isValid()) {
                message.warning("Invalid date range selected");
                setDownloadLoading(false);
                return;
            }

            // Resolve Source Hubs (selectedPremiseHubs) for Download logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedPremiseIds = selectedPremiseHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedPremiseHubs;

            const params = {
                fromDate: startDate.format("YYYY-MM-DD"),
                toDate: endDate.format("YYYY-MM-DD"),
                ...(resolvedPremiseIds.length > 0 && { premiseIds: resolvedPremiseIds.join(",") }),
                ...(selectedDestinationHubs.length > 0 && { destinationHubIds: selectedDestinationHubs.join(",") })
            };

            console.log("Download params:", params);

            await downloadFinalizedBagsPendingOutscanReport(params);
            message.success("Report download initiated");
        } catch (error) {
            console.error("Error downloading report:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Finalized Bags Pending Outscan Report" />
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={(value) => {
                                setDateRange(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Source Hubs</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Source Hubs"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedPremiseHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedPremiseHubs}
                            mode="multiple"
                            allowClear
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Destination Hubs</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Destination Hubs"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedDestinationHubs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedDestinationHubs}
                            mode="multiple"
                            allowClear
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">&nbsp;</div>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handleDownload}
                            loading={downloadLoading}
                            block
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>

                <Row style={{ marginTop: "24px" }}>
                    <Col span={24}>
                        <TableComponent
                            columns={columns}
                            dataSource={reportData}
                            loading={loading}
                            pagination={pagination}
                            onChange={handleTableChange}
                            rowKey="bagNumber"
                            scroll={{ x: 1500 }}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default FinalizedBagsPendingOutscanReport;

